import {
  createAsyncThunk,
  createEntityAdapter,
  createSelector,
  createSlice,
} from '@reduxjs/toolkit';
import axios from 'axios';
import formatISO from 'date-fns/formatISO';
import { selectSelectedCycles } from './cyclesSlice';
import { showMessage } from "../../../store/BASE/messageSlice";

export const dateFormat = 'yyyy-MM-dd HH:mm:ss.SSSSSS';

const convertTitletoReview = (name) => {
  switch (name) {
    case 'SB ALPHA Development':
      return 'dev_alpha_internal';
    case 'SB ALPHA Review':
      return 'dev_alpha';
    case 'SB ALPHA Revision':
      return 'dev_alpha_revision';
    case 'SB BETA Development':
      return 'dev_beta_internal';
    case 'SB BETA Review':
      return 'dev_beta';
    case 'SB BETA Revision':
      return 'dev_beta_revision';
    case 'SB Final Review and Acceptance':
      return 'dev_final';
    case 'IMI ALPHA Development':
      return 'prod_alpha_internal';
    case 'IMI ALPHA Review':
      return 'prod_alpha';
    case 'IMI ALPHA Revision':
      return 'prod_alpha_revision';
    case 'IMI BETA Development':
      return 'prod_beta_internal';
    case 'IMI BETA Review':
      return 'prod_beta';
    case 'IMI BETA Revision':
      return 'prod_beta_revision';
    case 'IMI Final Review and Acceptance':
      return 'prod_final';
    case 'Pre-Trials QA':
      return 'trials_pre';
    case 'Individual Trials Review':
      return 'trials_individual';
    case 'Individual Trials Revision':
      return 'trials_individual_revision';
    case 'Group Trials Review':
      return 'trials_group';
    case 'Group Trials Revision':
      return 'trials_group_revision';
    case 'Trials Final Review and Acceptance':
      return 'trials_final';
  }
}

const convertTitletoInternailty = (name) => {
  switch (name) {
    case 'ALPHA Review':
      return false;
    case 'BETA Review':
      return false;
    case 'Final Review and Acceptance':
      return false;
    default:
      return true;
  }
}

const convertLabeltoLesson = (name) => {
  switch (name) {
    case 'RU-M01':
      return 'lessons 1';
    case 'RU-M01-A':
      return 'lessons 2';
    case 'RU-M02':
      return 'lessons 3';
    case 'RU-M02-A':
      return 'lessons 4';
    case 'RU-M03':
      return 'lessons 5';
    case 'RU-M03-A':
      return 'lessons 6';
    case 'RU-M04':
      return 'lessons 7';
    case 'RU-M04-A':
      return 'lessons 8';
    case 'RU-M05':
      return 'lessons 9';
    case 'RU-M05-A':
      return 'lessons 10';
    case 'RU-M06':
      return 'lessons 11';
    case 'RU-M06-A':
      return 'lessons 12';
    case 'AD-M01':
      return 'lessons 13';
    case 'AD-M01-A':
      return 'lessons 14';
    case 'AD-M02':
      return 'lessons 15';
    case 'AD-M02-A':
      return 'lessons 16';
    case 'AD-M03':
      return 'lessons 17';
    case 'AD-M03-A':
      return 'lessons 18';
    case 'AD-M04':
      return 'lessons 19';
    case 'AD-M04-A':
      return 'lessons 20';
    case 'AD-M05':
      return 'lessons 21';
    case 'AD-M05-A':
      return 'lessons 22';
    case 'AD-M06':
      return 'lessons 23';
    case 'AD-M06-A':
      return 'lessons 24';
    case 'KP-M01':
      return 'lessons 25';
    case 'KP-M01-A':
      return 'lessons 26';
    case 'KP-M02':
      return 'lessons 27';
    case 'KP-M02-A':
      return 'lessons 28';
    case 'KP-M03':
      return 'lessons 29';
    case 'KP-M03-A':
      return 'lessons 30';
    case 'KP-M04':
      return 'lessons 31';
    case 'KP-M04-A':
      return 'lessons 32';
    case 'KP-M05':
      return 'lessons 33';
    case 'KP-M05-A':
      return 'lessons 34';
    case 'KP-M06':
      return 'lessons 35';
    case 'KP-M06-A':
      return 'lessons 36';
    case 'RU-PT':
      return 'lessons 37';
    case 'AD-PT':
      return 'lessons 38';
    case 'KP-PT':
      return 'lessons 39';
    case 'RU-M00':
      return 'lessons 40';
    case 'AD-M00':
      return 'lessons 41';
    case 'KP-M00':
      return 'lessons 42';
  }
}

const formatTimestamp = (timestamp) => {
  const date = new Date(Number(timestamp));

  // Ensure the date is valid
  if (isNaN(date.getTime())) {
      throw new Error("Invalid timestamp");
  }

  // Get components of the date
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0'); // Months are 0-based
  const day = String(date.getDate()).padStart(2, '0');
  const hours = String(date.getHours()).padStart(2, '0');
  const minutes = String(date.getMinutes()).padStart(2, '0');
  const seconds = String(date.getSeconds()).padStart(2, '0');

  // Get timezone offset in hours and minutes
  const offset = -date.getTimezoneOffset(); // Minutes from UTC
  const offsetSign = offset >= 0 ? '+' : '-';
  const offsetHours = String(Math.floor(Math.abs(offset) / 60)).padStart(2, '0');
  const offsetMinutes = String(Math.abs(offset) % 60).padStart(2, '0');

  // Construct the final ISO 8601 string
  return `${year}-${month}-${day}T${hours}:${minutes}:${seconds}${offsetSign}${offsetHours}:${offsetMinutes}`;
};

const convertReviewsToEvents = (pulledReviews, lessondata) => {
  // create a new event array
  const events = [];
  const seedData = [];
  // find the lesson that matches the review pmt_id
  const findLessons = (pmt_id) => {
    return lessondata.filter((lesson) => lesson.pmt_id.production === pmt_id)
  };
  // loop through the data and create a new event object
  pulledReviews.forEach((item) => {
    // find the lesson that matches the review pmt_id
    const lessons = findLessons(item.parent);
    // loop through the lessons and create a new event object
    lessons.forEach((lesson) => {
      events.push({
        id: item.id,
        // course: convertLabeltoCourse(lesson.course.label),
        lesson: convertLabeltoLesson(lesson.label),
        lesson_id: lesson.id,
        start: formatTimestamp(item.start_date),
        end: formatTimestamp(item.due_date),
        cycle: convertTitletoReview(item.name),
        pmt_id: item.id,
        internality: convertTitletoInternailty(item.name),
      });

      seedData.push({
        // course: convertLabeltoCourse(lesson.course.label),
        lesson: convertLabeltoLesson(lesson.label),
        start: formatTimestamp(item.start_date),
        end: formatTimestamp(item.due_date),
        cycle: convertTitletoReview(item.name),
        pmt_id: item.id,
        internality: convertTitletoInternailty(item.name),
      });

    });
  });

  return events;
}

export const getClickUpReviews = createAsyncThunk(
  'reviews/getReviews', 
  async (params, {dispatch, getState}) => {
    try {
      // send request to get lessons
      const lessonResponse = await axios.get(
        `${process.env.REACT_APP_API_SERVICE_URL}/api/lessons`
      );
      // get response data
      const lessonData = await lessonResponse.data;

      const listId = '901405897307';
      const clickUpReviewResponse = await fetch(
        `https://api.clickup.com/api/v2/list/${listId}/task?subtasks=true&order_by=due_date&reverse=true&custom_items[]=0&tags[]=client&tags[]=contractor&page=1`,
        {
          method: 'GET',
          headers: {
            Authorization: 'pk_82489691_TXFMIHV9UC4OD6K1CITPB6BTVQRY09XJ'
          }
        }
      );

      const clickUpReviewData = await clickUpReviewResponse.json();

      const reviews = convertReviewsToEvents(clickUpReviewData.tasks, lessonData);
      // return data
      return reviews;
    } catch (error) {
      // show error message
      dispatch(showMessage({message: error.message, variant: 'error'}));
    }
  }
);

export const getReviews = createAsyncThunk(
  "reviews/getReviews",
  async (params, { getState }) => {
    const response = await axios.get(
      `${process.env.REACT_APP_API_SERVICE_URL}/api/reviews`
    );

    const data = await response.data;

    return { data };
  }
);

export const addReview = createAsyncThunk(
  'reviews/addReview',
  async (newReview, { dispatch, getState }) => {
    try {
      // set user id
      newReview.user = getState().user.id;
      // send request to add review
      const response = await axios.post(
        `${process.env.REACT_APP_API_SERVICE_URL}/api/reviews`, newReview
      );
      // get response data
      const data = await response.data;
      // show success message
      dispatch(showMessage({ message: 'Review Added' }));
      // return data
      return data;
    } catch (error) {
      // if error, show error message
      dispatch(showMessage({ message: 'Review Not Added', variant: 'error' }));
      // return null
      return null;
    }
  }
);

export const updateReview = createAsyncThunk(
  'reviews/updateReview',
  async (review, { dispatch, getState }) => {
    try {
      // set user id
      review.user = getState().user.id;
      // send request to update review
      const response = await axios.patch(
        `${process.env.REACT_APP_API_SERVICE_URL}/api/reviews/${review.id}`,
        review
      );
      // get response data
      const data = await response.data;
      // show success message
      dispatch(showMessage({ message: 'Review Updated' }));
      // return data
      return data;
    } catch (error) {
      // if error, show error message
      dispatch(showMessage({ message: 'Review Not Updated', variant: 'error' }));
      // return null
      return null;
    }
  }
);

export const removeReview = createAsyncThunk(
  'reviews/removeReview',
  async (id, { dispatch }) => {
    try {
      // send request to remove review
      const response = await axios.delete(
        `${process.env.REACT_APP_API_SERVICE_URL}/api/reviews/${id}`
      );
      // get response data
      await response.data;
      // show success message
      dispatch(showMessage({ message: 'Review Removed' }));
      // return data
      return id;
    } catch (error) {
      // if error, show error message
      dispatch(showMessage({ message: 'Review Not Removed', variant: 'error' }));
      // return null
      return null;
    }
  }
);

// create entity adapter
const reviewsAdapter = createEntityAdapter();

export const {
  selectAll: selectReviews,
  selectIds: selectReviewIds,
  selectById: selectReviewById,
} = reviewsAdapter.getSelectors((state) => state.reviews.reviews);

const reviewsSlice = createSlice({
  name: 'reviews',
  initialState: reviewsAdapter.getInitialState({
    reviewDialog: {
      type: 'new',
      props: {
        open: false,
        anchorPosition: { top: 200, left: 400 },
      },
      data: null,
    },
  }),
  reducers: {
    openNewReviewDialog: {
      prepare: (selectInfo) => {
        const { start, end, jsEvent } = selectInfo;
        const payload = {
          type: 'new',
          props: {
            open: true,
            anchorPosition: { top: jsEvent.pageY, left: jsEvent.pageX },
          },
          data: {
            start: formatISO(new Date(start), { fractionDigits: 6 }),
            end: formatISO(new Date(end), { fractionDigits: 6 }),
          },
        };
        return { payload };
      },
      reducer: (state, action) => {
        state.reviewDialog = action.payload;
      },
    },
    openEditReviewDialog: {
      prepare: (clickInfo) => {
        const { jsEvent, event } = clickInfo;
        
        const { id, start, end, extendedProps } = event;
        const { lessons, cycle, pmt_id, internality } = extendedProps;
        // // const course_id = course.id;
        // const lesson_id = lesson.id;

        const payload = {
          type: 'edit',
          props: {
            open: true,
            anchorPosition: { top: jsEvent.pageY, left: jsEvent.pageX },
          },
          data: {
            id,
            // course: course_id,
            lessons,
            cycle,
            start: formatISO(new Date(start)),
            end: formatISO(new Date(end)),
            pmt_id,
            internality,
          },
        };
        return { payload };
      },
      reducer: (state, action) => {
        state.reviewDialog = action.payload;
      },
    },
    closeNewReviewDialog: (state, action) => {
      state.reviewDialog = {
        type: 'new',
        props: {
          open: false,
          anchorPosition: { top: 200, left: 400 },
        },
        data: null,
      };
    },
    closeEditReviewDialog: (state, action) => {
      state.reviewDialog = {
        type: 'edit',
        props: {
          open: false,
          anchorPosition: { top: 200, left: 400 },
        },
        data: null,
      };
    },
  },
  extraReducers: {
    [addReview.fulfilled]: reviewsAdapter.addOne,
    [updateReview.fulfilled]: reviewsAdapter.upsertOne,
    [removeReview.fulfilled]: reviewsAdapter.removeOne,
    [getReviews.fulfilled]: (state, action) => {
      const { data } = action.payload;
      reviewsAdapter.setAll(state, data);
    },
  },
});

export const {
  openNewReviewDialog,
  closeNewReviewDialog,
  openEditReviewDialog,
  closeEditReviewDialog,
} = reviewsSlice.actions;



export const selectFilteredReviews = createSelector(
  [selectSelectedCycles, selectReviews],
  (selectedCycles, reviews) => {
    // return reviews;
    return reviews.filter((item) => selectedCycles.includes(item.cycle));
  }
);

export const selectReviewDialog = ({ reviews }) => reviews.reviews.reviewDialog;

export default reviewsSlice.reducer;
